// The warmup page's words and colours, apart from its components so that the
// rules in them — what a zone is coloured, what "nincs adat" means, what a
// release will do — run on Node in tests/admin.
//
// Nothing here decides a zone. Rule 4's comparison is the API's (the rule's
// own exact ratios, `emc_api.warmup_view.zone`); this only prints the number
// and picks the tone the zone already is.

import type {
  RateZone,
  WarmupCurvePoint,
  WarmupEvent,
  WarmupIsp,
  WarmupPlan,
  WarmupPool,
  WarmupProjection,
  WarmupRates,
  WarmupThresholds,
} from "@/lib/api";
import type { CurvePoint } from "@/components/emc/charts/Curve";
import { count, share } from "@/components/emc/format";
import { labelOf, statusLook } from "@/components/emc/status";
import type { Tone } from "@/components/emc/status";
import { reasonText } from "@/pages/warmup/reasons";

// --- rates ----------------------------------------------------------------------

/** A zone's tone. Under the raise line is the ordinary state and gets no
 * colour (DESIGN.md: colour carries meaning); between the lines needs a look;
 * past the pause line is the stop. */
export const ZONE_TONE: Record<RateZone, Tone> = {
  clear: "muted",
  between: "warn",
  over: "danger",
  none: "muted",
};

export type RateKind = "bounce" | "complaint";

/** Rule-4 permille, printed as a percentage: 20 → "2%", 1 → "0,1%". */
export function permilleText(permille: number): string {
  return `${(permille / 10).toLocaleString("hu-HU", { maximumFractionDigits: 1 })}%`;
}

/** The two lines one rate is measured against, as a caption. */
export function thresholdLine(kind: RateKind, thresholds: WarmupThresholds): string {
  const below = kind === "bounce" ? thresholds.raise_bounce_below : thresholds.raise_complaint_below;
  const above = kind === "bounce" ? thresholds.pause_bounce_above : thresholds.pause_complaint_above;
  return `emelés < ${permilleText(below)} · megállás > ${permilleText(above)}`;
}

export interface RateCell {
  /** "0,6%", or the words for why there is no number. */
  text: string;
  tone: Tone;
  /** What the colour means, for the hover. */
  title: string;
}

const ZONE_SENTENCE: Record<RateZone, string> = {
  clear: "az emelési küszöb alatt",
  between: "az emelési és a megállítási küszöb között: ezen a napon nem emelne",
  over: "a megállítási küszöb fölött",
  none: "nem ment ki levél",
};

/** One rate of one day, ready for a pill.
 *
 * Nothing known is "nincs adat" and a day nothing was sent on is "nincs
 * küldés" — neither is ever "0%", which would claim a clean day. */
export function rateCell(
  rates: WarmupRates | null,
  kind: RateKind,
  minSample: number,
): RateCell {
  if (rates === null) {
    return { text: "nincs adat", tone: "muted", title: "Erről a napról nincs újraszámolt adat." };
  }
  if (rates.sent <= 0) {
    return { text: "nincs küldés", tone: "muted", title: ZONE_SENTENCE.none };
  }
  const part = kind === "bounce" ? rates.bounced : rates.complained;
  const zone = kind === "bounce" ? rates.bounce_zone : rates.complaint_zone;
  const small = rates.sent < minSample;
  return {
    text: share(part, rates.sent) ?? "nincs adat",
    tone: ZONE_TONE[zone],
    title: `${count(part)} / ${count(rates.sent)}: ${ZONE_SENTENCE[zone]}${
      small ? ` — kevés minta (${count(minSample)} alatt az ütemező nem dönt rajta)` : ""
    }`,
  };
}

// --- today's budget --------------------------------------------------------------

export interface BudgetLook {
  tone: Tone;
  /** One or two words under the bar. */
  label: string;
  /** The sentence that explains the bar, or null when it explains itself. */
  note: string | null;
}

/** How today's bar reads: a stopped pool is a stop whatever its limit says.
 * `why` is the page's reason for a budget of 0 (`simple.poolZeroReason`):
 * a 0 is never shown without one. */
export function budgetLook(pool: WarmupPool, why: string | null = null): BudgetLook {
  if (pool.is_paused) {
    return {
      tone: "danger",
      label: "áll",
      note: "A pool áll: a limiter semmit nem enged ki, amíg valaki fel nem oldja.",
    };
  }
  if (pool.budget_today <= 0) {
    return {
      tone: "muted",
      label: "nincs keret",
      note:
        pool.warmup_state === "cold"
          ? "A pool hideg: amíg a warmup nem indul, semmi nem megy ki rajta."
          : (why ?? "A mai keret nulla: semmi nem megy ki rajta."),
    };
  }
  const sent = pool.today.rates?.sent ?? 0;
  if (sent > pool.budget_today) {
    return { tone: "danger", label: "túllépve", note: "Többet küldött, mint a mai keret." };
  }
  if (pool.throttle_factor < 1) {
    return {
      tone: "warn",
      label: "lassítva",
      note: `Az őrszem a warmup keretének ${Math.round(pool.throttle_factor * 100)}%-át engedi (${count(
        pool.full_budget_today,
      )} helyett ${count(pool.budget_today)}).`,
    };
  }
  return { tone: "running", label: "megy", note: null };
}

/** The line under the budget bar: the share with what it is a share of
 * (DESIGN.md: "a napi keret 52%-a", not a bare number), the limit and the
 * target — or, for a stopped pool, what the budget will be once it is let go. */
export function budgetCaption(pool: WarmupPool, why: string | null = null): string {
  const limit = `${count(pool.daily_limit_per_ip)} / IP / nap`;
  if (pool.is_paused) {
    return `áll — feloldás után ${count(pool.budget_today)} a keret (${limit})`;
  }
  const sent = pool.today.rates?.sent ?? 0;
  const ofBudget = share(sent, pool.budget_today);
  const parts = [
    ofBudget === null ? "nincs keret" : `a napi keret ${ofBudget}-a`,
    limit,
    `cél ${count(pool.target_daily_per_ip)}`,
  ];
  const look = budgetLook(pool, why);
  if (look.note) parts.push(look.note);
  return parts.join(" · ");
}

/** The tile under the limit: how far the ramp has come. */
export function stepText(pool: WarmupPool): string {
  const target = `cél ${count(pool.target_daily_per_ip)}`;
  if (pool.warmup_state === "cold") return `${target} · nem indult`;
  if (pool.warmup_step <= 0) return `${target} · még nem emelt`;
  return `${target} · ${pool.warmup_step} emelés eddig`;
}

/** "Ma eddig": today's running count, measured against what today allows. */
export function todaySoFar(pool: WarmupPool): string {
  if (pool.warmup_state === "cold") return "a pool nem küld";
  const sent = pool.today.rates?.sent ?? 0;
  if (pool.is_paused) return `${count(sent)} — a pool áll`;
  const ofBudget = share(sent, pool.budget_today);
  return `${count(sent)} — ${ofBudget === null ? "nincs keret" : `a napi keret ${ofBudget}-a`}`;
}

/** A day the pool was held back: the word, and its tone. */
export function heldLook(held: "paused" | "throttled" | null): { label: string; tone: Tone } | null {
  if (held === "paused") return { label: "állt", tone: "danger" };
  if (held === "throttled") return { label: "lassítva", tone: "warn" };
  return null;
}

// --- the curve --------------------------------------------------------------------

/** "09. 24." out of "2026-09-24". A day on the axis needs no year. */
export function dayLabel(iso: string): string {
  const [, month, day] = iso.split("-");
  if (!month || !day) return iso;
  return `${month}. ${day}.`;
}

/** The API's points as the Curve takes them. A missing actual stays missing:
 * a day nothing was recomputed for is a gap in the line, not a zero. The line
 * is whole days: today's running count would end every curve on a fall that is
 * only the hour of the day, so it is left to the budget bar, and the ringed
 * "now" lands on the last whole day. */
export function curveData(points: WarmupCurvePoint[], today: string): CurvePoint[] {
  return points.map((point) => ({
    label: dayLabel(point.day),
    actual: point.day < today ? point.actual : null,
    plan: point.plan,
  }));
}

/** Short form for the axis: 128 000 → "128 e", 2 400 000 → "2,4 M". */
export function compact(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString("hu-HU", { maximumFractionDigits: 1 })} M`;
  }
  if (Math.abs(value) >= 10_000) {
    return `${Math.round(value / 1_000).toLocaleString("hu-HU")} e`;
  }
  return value.toLocaleString("hu-HU");
}

/** "2026. 10. 03." → "okt. 3." — a day in a sentence. */
export function dayInWords(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("hu-HU", { month: "short", day: "numeric", timeZone: "UTC" });
}

export interface PlanLine {
  label: string;
  value: string;
}

/** What the dashed line is, and where the pool stands against it — as label
 * and value, so a date is never followed by a sentence's own full stop. */
export function planLines(
  plan: WarmupPlan | null,
  projection: WarmupProjection | null,
): PlanLine[] {
  if (plan === null) {
    return [
      {
        label: "Terv",
        value: "nincs — a poolnak nincs indítási sora, amiből a rámpa számolható",
      },
    ];
  }
  const growth = plan.growth.toLocaleString("hu-HU", { maximumFractionDigits: 2 });
  const ramp = plan.growth > 1 ? `tiszta nap után ×${growth}` : `×${growth}: nem emelkedik`;
  const lines: PlanLine[] = [
    {
      label: plan.hypothetical ? "Ha ma indul" : "Indulás",
      value: `${plan.hypothetical ? "" : `${dayInWords(plan.start_day)}, `}${count(
        plan.start_per_ip,
      )} / IP / nap, ${ramp}`,
    },
    {
      label: `A cél (${count(plan.target_per_ip)} / IP) a terv szerint`,
      value: plan.reaches_target_on ? dayInWords(plan.reaches_target_on) : "ezzel a szorzóval soha",
    },
  ];
  if (projection !== null && !plan.hypothetical) {
    lines.push({
      label: `A mai limitről (${count(projection.from_per_ip)} / IP), tiszta napokkal`,
      value:
        projection.days === 0
          ? "elérte"
          : projection.reaches_target_on
            ? dayInWords(projection.reaches_target_on)
            : "ezzel a szorzóval soha",
    });
  }
  return lines;
}

// --- actions ----------------------------------------------------------------------

/** The start dialog's optional per-IP limit: null is "the configured one".
 * Refused here with a sentence rather than by the API with a 422. The most a
 * start may ask for is `ceiling`: the configured start (EMC_WARMUP_START_PER_IP)
 * capped at the pool's target — the server's `decisions.start_ceiling`. A cold
 * pool earns more on the ramp, never on the start. */
export function perIpOf(text: string, ceiling: number): number | null {
  const trimmed = text.trim().replace(/\s/g, "");
  if (trimmed === "") return null;
  const value = Number(trimmed);
  if (!Number.isInteger(value) || value < 1) {
    throw new Error("Az induló limit egész szám, legalább 1. Hagyd üresen a beállított értékhez.");
  }
  if (value > ceiling) {
    throw new Error(
      `Az induló limit legfeljebb ${count(ceiling)} / IP: a beállított induló lépcső ` +
        "(EMC_WARMUP_START_PER_IP), a pool céljánál nem több. Innen a rámpa emel, tiszta napok után. " +
        "Már meleg IP-k limitjét a `warmup set-limit` parancs állítja, a neveddel.",
    );
  }
  return value;
}

export interface ReleaseCopy {
  description: string;
  /** Said before the button when the release will not hold. */
  warning: string | null;
}

/** When the scheduler next decides a released pool's limit. A pool stopped
 * over midnight has no decision for today (the midnight tick skips a paused
 * pool), and the first tick after the release makes it — on yesterday's
 * figures, within the minute, not at the next midnight. */
function nextDecision(pool: WarmupPool): string {
  return pool.decided_today
    ? "Az ütemező éjfélkor dönt újra."
    : "A mai döntés még hátravan: az ütemező a feloldás utáni percben meghozza, a tegnapi számok alapján.";
}

/** The sentence a stopped pool carries when a release would not hold: the
 * banner over the pool and the dialog say it in the same words. */
export function repauseWarning(pool: WarmupPool): string | null {
  if (!pool.repause_risk) return null;
  if (pool.repause_basis === "yesterday") {
    return (
      "A tegnapi számok a megállítási küszöb fölött vannak, és a mai döntés még hátravan: az ütemező a " +
      "feloldás utáni percben ezekre dönt, újra megállítja a poolt, és a limittel újra visszalép. " +
      "A feloldás holnap tart (KERDESEK 3.11)."
    );
  }
  return (
    "A mai számok a megállítási küszöb fölött vannak: az ütemező a következő percben újra " +
    "megállítja a poolt. A feloldás holnap, új számokon tart (KERDESEK 3.11)."
  );
}

/** What stopped or slowed the pool, as the page says it — null when nothing
 * on the row says. The scheduler's sentences in Hungarian, anybody else's as
 * they were written. */
export function stopReason(pool: Pick<WarmupPool, "paused_reason" | "watch">): string | null {
  const stored = pool.paused_reason || pool.watch.reason;
  return stored ? reasonText(stored) : null;
}

/** What "Feloldás" will do on this pool, in the words the dialog uses. */
export function releaseCopy(pool: WarmupPool): ReleaseCopy {
  const limit = `${count(pool.daily_limit_per_ip)} / IP`;
  const stopped = pool.warmup_state === "paused";
  const description =
    pool.release_path === "watch"
      ? `Az őrszem elengedi a poolt (${statusLook("poolWatch", pool.watch.state).label} → kézi feloldás → figyeljük), ` +
        `a keret újra teljes, és ha a warmup is áll, az is folytatódik ugyanazzal a jegyzettel, ${limit} limitről.` +
        (stopped ? ` ${nextDecision(pool)}` : "")
      : `A warmup folytatódik: megállítva → melegítés, ${limit} limitről. ${nextDecision(pool)}`;
  let warning = repauseWarning(pool);
  if (warning === null && pool.release_path === "watch" && pool.watch.reason && !pool.watch.actor.includes("@")) {
    // The watchdog held it on an outside signal; a person's own stop names
    // the person, and there is no signal to come back.
    warning = `Ha a kiváltó jel (${pool.watch.reason}) még fennáll, az őrszem a következő körében újra megállíthatja.`;
  }
  return { description, warning };
}

// --- the ISP split -----------------------------------------------------------------

export interface IspShare {
  isp: string;
  label: string;
  sent: number;
  share: string | null;
  bounce: string | null;
  complaint: string | null;
}

/** Each provider's share of the day, largest first; providers with nothing sent are left out. */
export function ispShares(rows: WarmupIsp[]): IspShare[] {
  const total = rows.reduce((sum, row) => sum + row.sent, 0);
  return rows
    .filter((row) => row.sent > 0)
    .sort((left, right) => right.sent - left.sent)
    .map((row) => ({
      isp: row.isp,
      label: labelOf("isp", row.isp),
      sent: row.sent,
      share: share(row.sent, total),
      bounce: share(row.bounced, row.sent),
      complaint: share(row.complained, row.sent),
    }));
}

// --- the log ------------------------------------------------------------------------

/** One log line's "what", in the words the state pills use. */
export function eventWhat(event: WarmupEvent): string {
  if (event.source === "alert") return event.kind ?? event.action;
  if (event.action === "rehearsal_started") return "próba kezdődik";
  if (event.action === "rehearsal_ended") return "próba vége";
  const domain = event.source === "watch" ? "poolWatch" : "warmup";
  const from = event.from_state ? statusLook(domain, event.from_state).label : "—";
  const to = event.to_state ? statusLook(domain, event.to_state).label : "—";
  return from === to ? to : `${from} → ${to}`;
}

/** A log line's "why". A scheduler's move carries its decision rather than a
 * sentence; the decision is the why, in the pill's word. A stored sentence of
 * the warmup's own is said in Hungarian (`reasons.ts`). */
export function eventWhy(event: WarmupEvent): string {
  if (event.text) return reasonText(event.text);
  if (event.decision) return `az ütemező napi döntése: ${statusLook("warmupDecision", event.decision).label}`;
  return "—";
}

// --- one SES account's number (2026-10-23) ------------------------------------

/** The number typed into an account's dialog: a whole number, at least `min`. */
export function accountNumberOf(text: string, min: number): number {
  const trimmed = text.trim().replace(/\s/g, "");
  const value = Number(trimmed);
  if (trimmed === "" || !Number.isInteger(value) || value < min) {
    throw new Error(`A napi szám egész szám, legalább ${min}.`);
  }
  return value;
}

/** A pool's part of the account's mailable contacts: "53%", "—" when none. */
export function shareOf(part: number, whole: number): string {
  if (whole <= 0) return "—";
  return `${Math.round((part / whole) * 100)}%`;
}

const ACCOUNT_EVENT_WORDS: Record<string, string> = {
  start: "indítás",
  decision: "napi döntés",
  step_back: "visszalépés megállítás után",
  limit: "kézi szám",
};

/** One line of an account's log: what moved the number, from what to what. */
export function accountEventWords(event: { action: string; old: number | null; new: number | null }): string {
  const what = ACCOUNT_EVENT_WORDS[event.action] ?? event.action;
  if (event.old === null || event.new === null) return what;
  return event.old === event.new ? `${what}: ${count(event.new)} marad` : `${what}: ${count(event.old)} → ${count(event.new)}`;
}
